/**
 * Generic data-fetching hook for API routes.
 *
 * Replaces the duplicated useState/useEffect/fetch pattern found in
 * useOrgDashboard, useOrgMembers, useOrgRequests, useOrgRequestDetail,
 * useOrgSettings, useProjects, and useProjectDetail.
 *
 * All our API routes return `{ data: T }` — this hook extracts `data`
 * automatically.
 *
 * Pass `null` as URL to skip fetching (e.g. when an ID isn't available yet).
 */

"use client";

import { useState, useEffect, useCallback, useRef } from "react";

interface UseApiQueryOptions {
  /** Fallback error message when the fetch fails without a specific message */
  errorMessage?: string;
}

interface UseApiQueryReturn<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const DEFAULT_ERROR = "Ein unerwarteter Fehler ist aufgetreten";

export function useApiQuery<T>(
  url: string | null,
  options?: UseApiQueryOptions
): UseApiQueryReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(url !== null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const errorMessage = options?.errorMessage ?? DEFAULT_ERROR;

  const fetchData = useCallback(async () => {
    if (!url) return;

    // Cancel any in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Nicht gefunden");
        }
        // Try to extract server error message
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error || errorMessage);
      }
      const json = await response.json();
      setData(json.data);
    } catch (err) {
      // Ignore abort errors
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(err instanceof Error ? err.message : errorMessage);
    } finally {
      // Only update loading if this controller wasn't aborted
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [url, errorMessage]);

  useEffect(() => {
    fetchData();

    return () => {
      abortRef.current?.abort();
    };
  }, [fetchData]);

  return { data, isLoading, error, refresh: fetchData };
}
